import { useState, useEffect } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currencies = [
  { key: "USD_INR", code: "USD", flag: "$" },
  { key: "EUR_INR", code: "EUR", flag: "\u20AC" },
  { key: "GBP_INR", code: "GBP", flag: "\u00A3" },
  { key: "SGD_INR", code: "SGD", flag: "S$" },
  { key: "THB_INR", code: "THB", flag: "\u0E3F" },
  { key: "MYR_INR", code: "MYR", flag: "RM" },
];

const CurrencyRates = () => {
  const { token } = useAuth();
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRates(); }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/currency-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRates(res.data);
    } catch {
      // silent fail
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid="currency-rates-loading">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-semibold text-gray-700">Live Exchange Rates</span>
        </div>
        <div className="space-y-2">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-6 bg-gray-50 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!rates) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid="currency-rates">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Live Exchange Rates</span>
          <span className="text-[10px] text-white bg-emerald-500 px-1.5 py-0.5 rounded-full font-medium">Live</span>
        </div>
        <button
          onClick={fetchRates}
          className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-emerald-600 transition-colors"
          title="Refresh rates"
          data-testid="refresh-rates-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-1.5">
        {currencies.map(({ key, code, flag }) => {
          const rate = rates[key];
          if (!rate) return null;
          return (
            <div
              key={key}
              className="flex items-center justify-between py-1.5 px-2.5 rounded-md hover:bg-gray-50 transition-colors"
              data-testid={`rate-${key}`}
            >
              <span className="text-sm text-gray-600">
                <span className="text-gray-400 mr-1">{flag}</span>
                1 {code}
              </span>
              <span className="text-sm font-bold text-gray-900 tabular-nums">
                = INR {rate.toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>
      {rates.updated_at && (
        <p className="text-[10px] text-gray-400 mt-2 text-right">
          Updated: {new Date(rates.updated_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
        </p>
      )}
    </div>
  );
};

export default CurrencyRates;
